import { IsRadioOrCheckboxGroupElementSymbol } from '@framework/form/item/symbols/isRadioOrCheckboxGroupElement.tsx';
import { useFormItemAccessibility } from '@framework/form/item/useFormItemAccessibility.tsx';
import clx from 'classnames';
import { omit } from 'lodash';
import styled from 'styled-components';
import { SelectListOption } from '../../common/option-list/SelectListOption.tsx';
import { getWidgetProps } from '../../common/util/getWidgetProps.ts';
import { type IMultiSelectWidget } from '../IMultiSelectWidget.ts';
import { type IUseMultiSelectWidgetOptionsReturn } from './hooks/useMultiSelectWidgetOptions.tsx';
import { MultiSelectOption } from './MultiSelectOption.tsx';

export interface IMultiSelectCheckboxComponentProps
  extends IUseMultiSelectWidgetOptionsReturn {
  widget: IMultiSelectWidget;
  readonly?: boolean;
}

const $CheckboxGroup = styled.div`
  display: flex;
  flex-flow: wrap;
  &.vertical {
    flex-direction: column;
    > label {
      width: max-content;
      max-width: 100%;
    }
  }
  &.read-only {
    pointer-events: none;
  }
`;

/**
 * Componente del widget MultiSelect di tipo Checkbox.
 * Gestisce la visualizzazione parsando le option e dividendole in gruppi.
 */
export function MultiSelectCheckboxComponent(
  props: IMultiSelectCheckboxComponentProps,
) {
  const { widget, componentOptions } = props;
  const widgetOptions = getWidgetProps(props);
  const vertical = widget.layout?.direction === 'vertical';

  const accessibility = useFormItemAccessibility();

  return (
    <div
      role="group"
      {...omit(accessibility.attributes, 'id', 'aria-required')}
      aria-labelledby={accessibility.labelId}
    >
      <$CheckboxGroup
        className={clx({ vertical, 'read-only': props.readonly })}
      >
        <SelectListOption
          readonly={props.readonly}
          widget={widget}
          options={componentOptions}
          OptionComponent={MultiSelectOption}
          widgetOptions={widgetOptions}
        />
      </$CheckboxGroup>
    </div>
  );
}

MultiSelectCheckboxComponent[IsRadioOrCheckboxGroupElementSymbol] = true;
